// Hooks
import { useEffect, useState, useCallback, useMemo } from "react";
import { useMedplum } from "@medplum/react";

// Types
import type { Goal } from "@medplum/fhirtypes";

// Services
import { searchGoals } from "@benchmark/core/services";

// Events
import { DomainEventNames } from "@benchmark/core/events";
import { useEventDrivenRefetch } from "@benchmark/core/hooks";

/**
 * Result type for usePatientGoals hook.
 * @agentic @benchmark/core
 */
export type UsePatientGoalsResult = {
  goals: Goal[];
  activeGoals: Goal[];
  completedGoals: Goal[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

/**
 * Fetches patient goals and filters into active and completed.
 * @agentic @benchmark/core
 */
export function usePatientGoals(patientId?: string): UsePatientGoalsResult {
  const medplum = useMedplum();

  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchGoals = useCallback(async () => {
    if (!patientId) {
      setGoals([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetchedGoals = await searchGoals(medplum, patientId, {
        _include: ["Goal:subject"],
        _sort: "-start-date",
      });

      setGoals(fetchedGoals);
    } catch (err) {
      console.error("Error fetching goals:", err);
      setError(err as Error);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, medplum]);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      void fetchGoals().catch((err) => {
        if (!cancelled) {
          console.error("Error in fetchGoals effect:", err);
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, [fetchGoals]);

  // Auto-refetch on goal events for this patient.
  useEventDrivenRefetch(
    [
      DomainEventNames.GoalCreated,
      DomainEventNames.GoalUpdated,
      DomainEventNames.GoalClosed,
      DomainEventNames.GoalProgressRecorded,
    ],
    {
      refetch: fetchGoals,
      enabled: !!patientId,
      filter: (payload) =>
        (payload as { patientId?: string }).patientId === patientId,
    },
  );

  // Filter active goals (lifecycleStatus === "active")
  // Memoize to prevent creating new array references on every render
  const activeGoals = useMemo(
    () => goals.filter((goal) => goal.lifecycleStatus === "active"),
    [goals],
  );

  // Filter completed goals (lifecycleStatus === "completed")
  // Memoize to prevent creating new array references on every render
  const completedGoals = useMemo(
    () => goals.filter((goal) => goal.lifecycleStatus === "completed"),
    [goals],
  );

  return {
    goals,
    activeGoals,
    completedGoals,
    loading,
    error,
    refetch: fetchGoals,
  };
}
